import {
  V1Container, V1ContainerPort, V1EnvFromSource, V1EnvVar, V1Lifecycle, V1Probe,
  V1ResourceRequirements, V1VolumeDevice, V1VolumeMount
} from "@kubernetes/client-node"
import type { Logger } from 'pino'
import { URL } from "url"
import { injectParamToCommandArgsOrEnv, metadataMatch } from "yam-operator/util"
import { ContainerEnvVar, KVString, PlanContextData } from "yam-types"
import { Settings } from "../settings.d"
import { ContainerPortSpec, ContainerResourceQuota, DeployWorkload, ExtraVolumeMount, PodContainerSpec } from "../types.d"
import { BUILT_IN_ENV_APP, BUILT_IN_ENV_NAMESPACE, BUILT_IN_ENV_NODE_IP, BUILT_IN_ENV_POD_ID, BUILT_IN_ENV_POD_IP, BUILT_IN_ENV_POD_NAME, BUILT_IN_ENV_PORT, BUILT_IN_ENV_VERSION, BUILT_IN_STATEFULSET_ID, CHECK_THRESHOLD_PREVENT_TRAFFIC, CHECK_THRESHOLD_RESTART, COMMON_PORT_NAME_MAPPING, DEFAULT_CHECK_INTERVAL_SECONDS, DEFAULT_CHECK_TIMEOUT, MIN_CHECK_DELAY_SECONDS } from "./constants"

export default class ContainerFactory {

  private planContextData: PlanContextData
  private workloadSpec: DeployWorkload
  private log: Logger
  private settings: Settings

  constructor(planContextData: PlanContextData, workloadSpec: DeployWorkload, settings: Settings) {
    this.planContextData = planContextData
    this.workloadSpec = workloadSpec
    this.settings = settings
  }

  setLogger(logger: Logger): void {
    this.log = logger
  }

  generatePodContainers(injectEnv: ContainerEnvVar[]): { initContainers: V1Container[], containers: V1Container[] } {
    const initContainers: V1Container[] = this.workloadSpec.initContainers as V1Container[] || []
    const containers: V1Container[] = []
    let idx = 0
    let idxInit = 1

    for (const c of this.workloadSpec.containers) {
      // 1. basic container properties
      let defaultImageNameSuffix = ''
      if (c.initContainer) {
        defaultImageNameSuffix = '-init-' + idxInit
      } else if (idx !== 0) {
        defaultImageNameSuffix = '-sidecar-' + idx
      }
      let image = c.image.indexOf(':') === -1 ? (c.image + ':' + (this.workloadSpec.version || 'latest')) : c.image
      // when base url not specified and default image base set, use default settings
      if (this.settings.defaultImageBase) {
        if (image.split(':')[0].split('/')[0].indexOf(".") === -1) {
          this.log.debug(`no dot found in container image: ${image}, will prepend default image base url.`)
          image = this.settings.defaultImageBase + "/" + image
        }
      }
      this.log.info(`container image retrieved: ${image}`)
      const name = c.name || this.planContextData.app + defaultImageNameSuffix


      const isLatestImage = image.endsWith('latest')
      // If no imagePullPolicy is specified,
      // then if the image is latest,
      // the imagePullPolicy is specified as 'always' otherwise 'IfNotPresent'
      const imagePullPolicy = c.imagePullPolicy ? c.imagePullPolicy : (isLatestImage ? 'Always' : 'IfNotPresent')
      const command = typeof c.command === "string" ? ['sh', '-c', '--', c.command] : c.command
      const securityContext = (c.privileged || c.securityContext) ? Object.assign(c.privileged ? { privileged: true } : {}, c.securityContext) : this.settings.defaultContainerSecurityContext
      const tmpContainer: V1Container = {
        // yam-engine built-in fields
        name,
        image,
        command,
        args: c.args,
        env: c.env || [],
        imagePullPolicy,
        workingDir: c.workingDir,

        // compatible with native kubernetes API
        startupProbe: c.startupProbe as V1Probe,
        stdin: c.stdin as boolean,
        stdinOnce: c.stdinOnce as boolean,
        tty: c.tty as boolean,
        envFrom: c.envFrom as V1EnvFromSource[],
        securityContext,
        terminationMessagePath: c.terminationMessagePath as string,
        terminationMessagePolicy: c.terminationMessagePolicy as string,
        volumeDevices: c.volumeDevices as V1VolumeDevice[]
      }

      // 2. calculate resources quota
      if (c.quotas) {
        tmpContainer.resources = this.generateResourceQuota(c.quotas)
      }
      if (c.resources) {
        tmpContainer.resources = Object.assign({}, c.resources, tmpContainer.resources) as V1ResourceRequirements
      }

      // 3. container liveness & readiness probes
      const { liveness, readiness, startup } = this.generateContainerProbes(c)
      tmpContainer.livenessProbe = liveness
      tmpContainer.readinessProbe = readiness
      tmpContainer.startupProbe = startup

      // 4. build container ports/hostPorts
      if (c.ports) {
        tmpContainer.ports = c.ports
      } else if (c.port) {
        tmpContainer.ports = ContainerFactory.generatePortSpec(c.port)
      }

      // 5. lifecycle hooks
      if (c.lifecycleHook) {
        this.generateLifecycleHooks(tmpContainer, c)
      } else if (c.lifecycle) {
        tmpContainer.lifecycle = c.lifecycle as V1Lifecycle
      }

      // 6. add volumeMounts
      tmpContainer.volumeMounts = this.generateContainerVolumeMounts(c, this.workloadSpec.mounts)

      // 7. inject fieldRef and metadata
      this.injectFieldRefsToEnv(tmpContainer, injectEnv)

      // 8. add container to corresponding list
      c.initContainer ? initContainers.push(tmpContainer) : containers.push(tmpContainer)
      c.initContainer ? idxInit++ : idx++
    }

    if (this.settings.containerInjection) {
      this.settings.containerInjection.filter(c => {
        if (c.spec?.name) {
          return metadataMatch(this.planContextData.currentModelFull.metadata, c.metadataMatcher)
        } else {
          this.log.warn(`container injection part in plugin settings doesn't contains name, can not be injected`)
          return false
        }
      }).forEach(c => {
        const spec = c.spec as unknown as V1Container
        const injectNs = this.checkInjectNamespace(this.planContextData.namespace, c.includeNamespaces, c.excludeNamespace)
        if (!injectNs) {
          this.log.info(`namespace not match, skip inject container: ${spec.name}`)
        }
        const initNotInject = this.workloadSpec.advancedFeatures?.disableInitContainerInjection
        const sidecarNotInject = this.workloadSpec.advancedFeatures?.disableSideCarContainerInjection
        if (!initNotInject && c.initContainer) {
          initContainers.push(spec)
          this.log.info(`inject init container: ${spec.name}`)
        }
        if (!sidecarNotInject && !c.initContainer) {
          containers.push(spec)
          this.log.info(`inject sidecar container: ${spec.name}`)
        }
      })
    }

    if (this.settings.containerParamInjection) {
      this.settings.containerParamInjection
        .filter(c => metadataMatch(this.planContextData.currentModelFull.metadata, c.metadataMatcher))
        .forEach(inject => injectParamToCommandArgsOrEnv(
          inject.initContainer ? initContainers : containers, inject.injectParams, inject.appendMode, inject.targetEnvVars, inject.targetCommandBinary
        ))
    }
    return { initContainers, containers }
  }

  checkInjectNamespace(ns: string, includes?: string[], excludes?: string[]): boolean {
    // includes mode, only allowed namespaces will be injected
    if (includes) {
      return includes.includes(ns)
    }
    if (excludes) {
      return !excludes.includes(ns)
    }
    // inject all namespaces by default
    return true
  }

  generateResourceQuota(r: ContainerResourceQuota): V1ResourceRequirements | undefined {
    const quota = {
      requests: {} as KVString,
      limits: {} as KVString
    }
    let cpuMultiplier = 1
    let memoryMultiplier = 1
    if (r.burstMultiplier?.cpu) {
      cpuMultiplier = r.burstMultiplier.cpu
    }
    if (r.burstMultiplier?.memory) {
      memoryMultiplier = r.burstMultiplier.memory
    }
    if (r.cpu) {
      const result = this.extractRequestsAndLimits(r.cpu, cpuMultiplier)
      quota.requests.cpu = result.requests
      quota.limits.cpu = result.limits
    }
    if (r.memory) {
      const result = this.extractRequestsAndLimits(r.memory, memoryMultiplier)
      quota.requests.memory = result.requests
      quota.limits.memory = result.limits
    }
    // GPU doesn't support burst feature currently
    if (r.gpu) {
      if (r.gpuType === "amd") {
        quota.limits["amd.com/gpu"] = r.gpu.toString()
      } else {
        // defaults to widely used nvidia
        quota.limits["nvidia.com/gpu"] = r.gpu.toString()
      }
    }
    return quota
  }

  extractRequestsAndLimits(raw: string | number, multiplier: number): { requests: string, limits: string } {
    const m = raw.toString().trim()
    const tmp = m.split(/\d+/)
    const suffix = tmp[tmp.length - 1]
    const limits = parseInt((parseInt(m) * multiplier).toString()) + suffix
    this.log.debug(`convert raw resources quotas from '${raw}' to { requests: ${m}, limits: ${limits}}`)
    if (limits.indexOf('NaN') !== -1) {
      this.log.error(`converting quotas data failed, please check 'quotas' field or use native 'resources' field instead.`)
    }
    return { requests: m, limits }
  }

  generateContainerProbes(c: PodContainerSpec): { liveness?: V1Probe, readiness?: V1Probe, startup?: V1Probe } {
    const result = {
      liveness: c.livenessProbe as V1Probe,
      readiness: c.readinessProbe as V1Probe,
      startup: c.startupProbe as V1Probe
    }
    if (c.healthCheck && c.healthCheck.enable) {
      const check = c.healthCheck
      const initialDelay = check.initialDelaySeconds || this.workloadSpec.minReadySeconds || MIN_CHECK_DELAY_SECONDS
      const probe: V1Probe = {
        periodSeconds: check.intervalSeconds as number || check.periodSeconds || DEFAULT_CHECK_INTERVAL_SECONDS,
        timeoutSeconds: check.timeoutSeconds || DEFAULT_CHECK_TIMEOUT,
        initialDelaySeconds: initialDelay
      }
      if (check.command) {
        if (typeof check.command === 'string') {
          probe.exec = {
            command: ["sh", "-c", "--", check.command]
          }
        } else {
          probe.exec = { command: check.command }
        }
      } else if (check.url) {
        // must be complete url, etc. http://127.0.0.1/health
        const url = new URL(check.url)
        const port = new Object(parseInt(url.port || '80'))
        // localhost means Pod IP, it's default setting of Probe
        // checks are sent from Kubelet, if probe host is set to 127.0.0.1, 'connection refuse' error will happen
        const host = (url.hostname === "localhost" || url.hostname === "127.0.0.1") ? undefined : url.hostname
        if (url.protocol.startsWith('tcp')) {
          probe.tcpSocket = { port, host }
        } else {
          probe.httpGet = {
            host, port,
            path: url.pathname,
            // kubernetes will skip cert validation and send request if it's https
            scheme: url.protocol.startsWith('https') ? 'HTTPS' : 'HTTP'
          }
        }
      }
      check.onFailure?.forEach(onFail => {
        if (onFail.action === "preventTraffic") {
          // readiness probe
          result.readiness = Object.assign({
            failureThreshold: onFail.times || CHECK_THRESHOLD_PREVENT_TRAFFIC
          }, probe)
        } else if (onFail.action === "restart") {
          // liveness probe
          result.liveness = Object.assign({
            failureThreshold: onFail.times || CHECK_THRESHOLD_RESTART
          }, probe)
        }
      })
    }
    return result
  }

  static generatePortSpec(port: string | number | ContainerPortSpec[]): V1ContainerPort[] {
    if (typeof port === "number") {
      return [{
        protocol: 'TCP',
        name: 'tcp-' + port,
        containerPort: port
      }]
    } else if (typeof port === "string") {
      const portNum = parseInt(port)
      if (isNaN(portNum)) {
        if (!COMMON_PORT_NAME_MAPPING[port]) {
          throw new Error(`can not resolve port ${port}, not recognized`)
        }
        return [{
          protocol: 'TCP',
          name: port,
          containerPort: COMMON_PORT_NAME_MAPPING[port]
        }]
      } else {
        return this.generatePortSpec(portNum)
      }
    } else {
      return port.map(p => {
        return {
          protocol: p.protocol,
          name: p.name,
          containerPort: p.containerPort,
          hostPort: p.hostPort,
          hostIP: p.hostIP as string
        }
      })
    }
  }

  generateLifecycleHooks(tmpContainer: V1Container, c: PodContainerSpec): void {
    if (c.lifecycleHook) {
      tmpContainer.lifecycle = {}
      if (c.lifecycleHook.afterStart) {
        tmpContainer.lifecycle.postStart = {
          exec: {
            command: c.lifecycleHook.afterStart
          }
        }
      }
      if (c.lifecycleHook.beforeTerminate) {
        tmpContainer.lifecycle.preStop = {
          exec: {
            command: c.lifecycleHook.beforeTerminate
          }
        }
      }
    }
  }

  generateContainerVolumeMounts(c: PodContainerSpec, mounts?: ExtraVolumeMount[]): V1VolumeMount[] {
    const containerName = c.name
    const vMounts: V1VolumeMount[] = c.volumeMounts ? (c.volumeMounts as V1VolumeMount[]) : []
    mounts?.forEach(m => {
      if (m.container && !containerName) {
        return
      }
      if (m.container && containerName) {
        // check if matches containerName, if not, do not mount to that container
        if (typeof m.container === "string" && m.container !== containerName) {
          return
        }
        if (typeof m.container === "object" && !m.container.includes(containerName)) {
          return
        }
      }
      // if containerName matches or no container name specified, mount to it by default
      vMounts.push({
        mountPath: m.path,
        name: m.name,
        readOnly: m.readOnly,

        // compatible with kubernetes native API
        subPath: m.subPath as string,
        subPathExpr: m.subPathExpr as string,
        mountPropagation: m.mountPropagation as string,
      })
    })
    return vMounts
  }

  injectFieldRefsToEnv(c: V1Container, injectEnv: ContainerEnvVar[]): void {
    // inject application metadata and version
    c.env?.push({
      name: BUILT_IN_ENV_APP,
      value: this.planContextData.app
    })
    c.env?.push({
      name: BUILT_IN_ENV_NAMESPACE,
      value: this.planContextData.namespace
    })
    c.env?.push({
      name: BUILT_IN_ENV_VERSION,
      value: this.workloadSpec.version
    })
    // inject Pod name, Pod/Node IP
    c.env?.push({
      name: BUILT_IN_ENV_POD_NAME,
      valueFrom: {
        fieldRef: {
          fieldPath: "metadata.name"
        }
      }
    })
    c.env?.push({
      name: BUILT_IN_ENV_POD_IP,
      valueFrom: {
        fieldRef: {
          fieldPath: "status.podIP"
        }
      }
    })
    c.env?.push({
      name: BUILT_IN_ENV_POD_ID,
      valueFrom: {
        fieldRef: {
          fieldPath: "metadata.uid"
        }
      }
    })
    c.env?.push({
      name: BUILT_IN_ENV_NODE_IP,
      valueFrom: {
        fieldRef: {
          fieldPath: "status.hostIP"
        }
      }
    })
    if (this.workloadSpec.type === "StatefulSet") {
      c.env?.push({
        name: BUILT_IN_STATEFULSET_ID,
        valueFrom: {
          fieldRef: {
            fieldPath: "metadata.name"
          }
        }
      })
    }
    // inject environment hierarchy and other info based on plan context data
    for (const extraEnv of injectEnv) {
      const matchContainer = !extraEnv.containers || extraEnv.containers.find(e => c.name === e)
      if (matchContainer) {
        c.env?.push({
          name: extraEnv.name,
          value: extraEnv.value,
          valueFrom: extraEnv.valueFrom
        })
        this.log.debug(`match env var: ${extraEnv.name} in container ${c.name}, injected`)
      }
    }
    // inject SERVER_PORT env if primary port exists
    if (c.ports && c.ports[0]?.containerPort) {
      c.env?.push({
        name: BUILT_IN_ENV_PORT,
        value: c.ports[0]?.containerPort + ''
      })
    }
    // inject envs from settings
    if (this.settings.envInjection) {
      for (const inj of this.settings.envInjection) {
        c.env?.push(inj as V1EnvVar)
      }
    }
  }
}